/**
 * Beacon Connection Prefetch
 *
 * Fires at module evaluation time (before React mounts), but only once the page
 * is a real, user-present load (see `prerenderGuard`), to start the WebSocket
 * connection and send CONN_BEACON_FAST_START as early as possible.
 * By the time React renders and the BeaconJoinCoordinator mounts, the server
 * response may already be waiting in beaconResolveState.
 *
 * The coordinator always calls connectBeaconFastStart() itself too — the
 * connect() guard prevents duplicate WebSocket connections, so this prefetch
 * only wins time; it never causes conflicts.
 */
import { getPersistentPlayerId } from '../../api';
import { connectBeaconFastStart } from './realtimeMessaging';
import { whenHumanPageLoad } from '../../utils/prerenderGuard';

function getBeaconShareCodeFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(/^\/j\/([^/]+)/);
  return match?.[1] || undefined;
}

const shareCode = getBeaconShareCodeFromUrl();

if (shareCode) {
  // A speculative load (Safari prefetch/pre-render) must not start a session.
  // Keep the early-start intent, but wait for a real, visible user before
  // connecting. Deliberately non-blocking: module evaluation continues and the
  // connect fires only once the page is a genuine, user-present load.
  void whenHumanPageLoad().then(() => {
    const persistentId = getPersistentPlayerId();

    connectBeaconFastStart({
      shareCode,
      persistentId,
      tokenJwt: undefined,
      requestIceServers: true,
    });
  });
}
